import TokenService from './services/token.service';

// Data-grid tabs that show more than their own Datum.Type. Adspend covers
// Advertising as well — the two carry the same columns, and spend reporting is
// read across both. The Advertising tab stays narrow so it can still be read on
// its own. Keys and values are lowercase; callers pass lowercase too.
const TYPE_BUCKETS = {
	adspend: ['adspend', 'advertising'],
};

const FILTER_FIELDS = [
	{ label: 'Date From', keys: ['dateFrom'], always: true },
	{ label: 'Date To', keys: ['dateTo'], always: true },
	{ label: 'Search', keys: ['search'] },
	{ label: 'Media Type', keys: ['mediaType', 'mediatype'] },
	{ label: 'Brand', keys: ['brand'] },
	{ label: 'Media Source', keys: ['mediaSource', 'mediasource'] },
	{ label: 'Category', keys: ['category'] },
	{ label: 'Sub Category', keys: ['subCategory', 'subcategory'] },
	{ label: 'Type', keys: ['type'] },
	{ label: 'Sentiment', keys: ['sentiment'] },
	{ label: 'Author', keys: ['author'] },
	{ label: 'Topic', keys: ['topic'] },
	{ label: 'Advert Type', keys: ['advertType', 'adverttype'] },
	{ label: 'Language', keys: ['language'] },
	{ label: 'Country', keys: ['country'] },
	{ label: 'Region', keys: ['location', 'region'] },
];

const DETAIL_FIELDS = [
	{ label: 'Rows', keys: ['rows', 'rowCount', 'rowsRequested'] },
	{ label: 'Selected Rows', keys: ['selectedRows', 'selectionCount'] },
	{ label: 'Tab Type', keys: ['tabType'] },
	{ label: 'Grid Filter', keys: ['gridFilter'] },
];

const MAX_ACTIVITY_LENGTH = 1800;

/**
 * Resolve a tab's Type into the list of Datum.Type values it should display.
 * Unknown/empty tabs resolve to [] meaning "no type restriction".
 */
export const resolveTypeBucket = (type) => {
	if (!type) return [];
	const key = String(type).toLowerCase();
	return TYPE_BUCKETS[key] ?? [key];
};

const isEmptyFilterValue = (value) => {
	if (value === undefined || value === null || value === '') return true;
	if (Array.isArray(value)) return value.filter((item) => item !== undefined && item !== null && item !== '').length === 0;
	return false;
};

const firstValue = (source, keys) => {
	if (!source) return undefined;
	for (const key of keys) {
		if (source[key] !== undefined && source[key] !== null) {
			return source[key];
		}
	}
	return undefined;
};

const valueLabel = (value) => {
	if (value && typeof value === 'object') {
		return value.itemName ?? value.name ?? value.mediatypeName ?? value.mediaType ?? value.mediatype ?? value.id;
	}
	return value;
};

const cleanUniqueValues = (value) => {
	const values = Array.isArray(value) ? value : [value];
	const seen = new Set();
	const result = [];

	values.forEach((item) => {
		const label = valueLabel(item);
		if (label === undefined || label === null || label === '') return;
		const key = String(label);
		if (seen.has(key)) return;
		seen.add(key);
		result.push(key);
	});

	return result;
};

const collectExportMediaTypes = ({ filters = {}, availableMediaTypes = [], rows = [], selectedRows = [] } = {}) => {
	const selectedMediaTypes = cleanUniqueValues(firstValue(filters, ['mediaType', 'mediatype']));
	if (selectedMediaTypes.length > 0) return selectedMediaTypes;

	const selectedIds = new Set(cleanUniqueValues(selectedRows));
	if (selectedIds.size > 0) {
		const rowMediaTypes = cleanUniqueValues(
			(rows || [])
				.filter((row) => selectedIds.has(String(row?.id)))
				.map((row) => row?.mediatype ?? row?.mediaType)
		);
		if (rowMediaTypes.length > 0) return rowMediaTypes;
	}

	return cleanUniqueValues(availableMediaTypes);
};

export const buildPythiaExportFilters = (filters = {}, context = {}) => {
	const exportFilters = { ...filters };
	const mediaTypes = collectExportMediaTypes({
		filters: exportFilters,
		availableMediaTypes: context.availableMediaTypes,
		rows: context.rows,
		selectedRows: context.selectedRows,
	});

	if (mediaTypes.length > 0) {
		exportFilters.mediaType = mediaTypes;
	}

	return exportFilters;
};

const formatFilterValue = (value) => {
	if (isEmptyFilterValue(value)) return 'All';
	if (Array.isArray(value)) return value.map(formatFilterValue).join(', ');
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? 'All' : value.toISOString().slice(0, 10);
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (typeof value === 'object') {
		if (value.itemName) return value.itemName;
		if (value.name) return value.name;
		if (value.id !== undefined) return value.id;
		return JSON.stringify(value);
	}
	if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
	return String(value);
};

const buildFieldParts = (fields, source) => fields
	.map((field) => {
		const value = firstValue(source, field.keys);
		if (!field.always && isEmptyFilterValue(value)) return null;
		return `${field.label}=${formatFilterValue(value)}`;
	})
	.filter(Boolean);

export const buildPythiaFilterSummary = (filters = {}, details = {}) => {
	const parts = [
		...buildFieldParts(FILTER_FIELDS, filters),
		...buildFieldParts(DETAIL_FIELDS, details),
	];

	return parts.length ? parts.join('; ') : '';
};

export const addPythiaFilterActivity = (activityName, filters = {}, details = {}) => {
	const summary = buildPythiaFilterSummary(filters, details);
	const activity = summary ? `${activityName}: ${summary}` : activityName;
	addUserActivity(activity.length > MAX_ACTIVITY_LENGTH ? `${activity.slice(0, MAX_ACTIVITY_LENGTH - 3)}...` : activity);
};

export const addUserActivity = (activityName) => {
	// UserTabs may be absent when a session is restored without mounting the Login
	// screen (it's only seeded there). Default to [] and guard the element access so
	// activity logging never throws — it must not break callers like Excel export.
	const UserTabs = JSON.parse(sessionStorage.getItem("UserTabs")) || [];
	UserTabs[UserTabs.length - 1]?.user_Tab_Activitties?.push({
		activity: activityName,
		createdDate: new Date(),
	});
	sessionStorage.setItem("UserTabs", JSON.stringify(UserTabs));
};

export const addViewedClip = (source, ID) => {
	const UserTabs = JSON.parse(sessionStorage.getItem("UserTabs")) || [];
	const user = TokenService.getUser();
	UserTabs[UserTabs.length - 1]?.viewed_clips?.push({
		publication: source,
		clipID: ID,
		username: user.userName,
		email: user.email,
		createdDate: new Date(),
	});
	sessionStorage.setItem("UserTabs", JSON.stringify(UserTabs));
};
